import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listMembers,
  listSurveyAccess,
  setSurveyAccess,
  removeSurveyAccess,
  getErrorMessage,
  type WorkspaceRole,
} from '@/lib/api';
import { Icon, Badge, Loading } from '@/components/ui';

const SHARE_ROLES: WorkspaceRole[] = ['editor', 'analyst', 'viewer'];
const ROLE_LABEL: Record<WorkspaceRole, string> = {
  owner: 'Propriétaire', editor: 'Éditeur', analyst: 'Analyste', viewer: 'Lecteur',
};

/**
 * Dialogue de partage d'UNE enquête avec des membres du workspace (B7e).
 * Octroi d'un accès override (rôle par enquête) + retrait. Le membre doit déjà
 * appartenir au workspace (sinon : l'inviter via la page Membres).
 */
export function ShareSurveyDialog({
  open,
  workspaceId,
  surveyId,
  surveyTitle,
  onClose,
}: {
  open: boolean;
  workspaceId: string;
  surveyId: string;
  surveyTitle: string;
  onClose: () => void;
}): JSX.Element | null {
  const qc = useQueryClient();
  const [memberId, setMemberId] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('analyst');
  const [error, setError] = useState<string | null>(null);

  const membersQ = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => listMembers(workspaceId),
    enabled: open,
  });
  const accessQ = useQuery({
    queryKey: ['survey-access', workspaceId, surveyId],
    queryFn: () => listSurveyAccess(workspaceId, surveyId),
    enabled: open,
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['survey-access', workspaceId, surveyId] });
  };

  const grantMut = useMutation({
    mutationFn: () => setSurveyAccess(workspaceId, surveyId, memberId, role),
    onSuccess: () => { setMemberId(''); setError(null); invalidate(); },
    onError: (err) => setError(getErrorMessage(err)),
  });
  const revokeMut = useMutation({
    mutationFn: (creatorId: string) => removeSurveyAccess(workspaceId, surveyId, creatorId),
    onSuccess: invalidate,
    onError: (err) => setError(getErrorMessage(err)),
  });

  if (!open) return null;

  // Membres non-owner (les owners ont déjà tout accès) sans accès déjà défini.
  const sharedIds = new Set((accessQ.data ?? []).map((a) => a.creatorId));
  const candidates = (membersQ.data ?? []).filter((m) => m.role !== 'owner' && !sharedIds.has(m.creatorId));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-text/40 backdrop-blur-sm animate-fade-in"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={`Partager ${surveyTitle}`}
        className="w-full max-w-lg bg-surface rounded-xl border border-border shadow-elevated p-6 space-y-4">
        <header>
          <h2 className="text-lg font-semibold">Partager l'enquête</h2>
          <p className="text-sm text-text-muted mt-0.5 truncate">{surveyTitle}</p>
        </header>

        {error && <p role="alert" className="field-error"><Icon.AlertTriangle /><span>{error}</span></p>}

        {/* Octroi */}
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="field flex-1">
            <label htmlFor="share-member" className="field-label">Membre</label>
            <select id="share-member" className="input-base" value={memberId}
              onChange={(e) => setMemberId(e.target.value)}>
              <option value="">— Choisir un membre —</option>
              {candidates.map((m) => <option key={m.creatorId} value={m.creatorId}>{m.email}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="share-role" className="field-label">Rôle sur l'enquête</label>
            <select id="share-role" className="input-base" value={role}
              onChange={(e) => setRole(e.target.value as WorkspaceRole)}>
              {SHARE_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </div>
          <button type="button" className="btn-primary" disabled={!memberId || grantMut.isPending}
            onClick={() => grantMut.mutate()}>
            Partager
          </button>
        </div>
        {candidates.length === 0 && membersQ.data && (
          <p className="text-xs text-text-muted">
            Aucun membre disponible. Invitez d'abord des collaborateurs via la page Membres.
          </p>
        )}

        {/* Accès existants */}
        <div className="border-t border-border pt-3">
          <h3 className="text-sm font-medium mb-2">Accès accordés</h3>
          {accessQ.isPending && <Loading />}
          {accessQ.data && accessQ.data.length === 0 && (
            <p className="text-sm text-text-muted">Cette enquête n'est partagée avec personne (hors propriétaires).</p>
          )}
          <ul className="space-y-1">
            {(accessQ.data ?? []).map((a) => (
              <li key={a.creatorId} className="flex items-center gap-3 py-1.5">
                <span className="flex-1 min-w-0 text-sm truncate">{a.email}</span>
                <Badge tone="primary">{ROLE_LABEL[a.role]}</Badge>
                <button type="button" className="btn-ghost btn-sm text-danger-text"
                  onClick={() => revokeMut.mutate(a.creatorId)} disabled={revokeMut.isPending}>
                  Retirer
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-end pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
